import {
    ChangeDetectionStrategy,
    Component,
    inject,
    Injector,
    Input,
    NgZone,
    OnChanges,
    OnInit,
    output,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { FiInfiniteScrollDataSource, TSortOrder } from '@fi-shared/fi-items-list/fi-infinite-scroll-data-source';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { MatSortDirection, ObjectTypes } from '@fi-shared/types';
import { MatSort } from '@angular/material/sort';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import { IChecklist2ReportHistoryUnits } from '../../../../shared/fi-checklist2-report';
import { FiChecklist2ReportHelperService } from '../../../../services/fi-checklist2-report-helper.service';
import { IChecklistStatusProgressData } from '@fi-shared/checklist/checklist-v2';
import { isDefined } from '@fi-shared/utils';
import { ChecklistReportPastChecklistChartSegmentIndex } from '@fi-shared/checklist/fi-checklist2-report/enums/fi-checklist2-report-chart-index-enum';
import { takeUntil } from 'rxjs/operators';
import { Checkpoint2StatusType } from '@fi-shared/checklist/checklist-enum';
import { Observable } from 'rxjs';

@Component({
    selector: 'fi-history-checklist-details-units-table',
    templateUrl: './history-checklist-units-table.component.html',
    styleUrls: ['./history-checklist-units-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-history-checklist-details-units-table' },
    standalone: false,
})
export class FiHistoryChecklistUnitsTableComponent
    extends FiInfiniteScrollDataSource<IChecklist2ReportHistoryUnits>
    implements OnInit, OnChanges
{
    @Input({ required: true }) checklistUnits$: Observable<IChecklist2ReportHistoryUnits[]>;
    @Input() iterationStatusType: ChecklistReportPastChecklistChartSegmentIndex;
    @Input() isDetailedAnswersReportAvailable: boolean;
    @Input() currentCheckpointStatusTypesFilter: Checkpoint2StatusType[] = [];
    @Input() isComplianceThresholdDataShown: boolean;
    @Input() isNonComplianceChecklistDataShown: boolean;

    public sortChange = output<void>();
    public onShowUnitMetrics = output<IChecklist2ReportHistoryUnits>();
    public onShowDetailedAnswersReportByBusinessUnitId = output<number>();

    private checklistUnits: IChecklist2ReportHistoryUnits[] = [];
    public readonly defaultSortColumnName: string = 'BusinessUnitName';
    public readonly itemsListType = FiItemsListType.ChecklistReportHistoryUnitsTable;
    public readonly objectType: ObjectTypes = ObjectTypes.Checklist2;

    private readonly _iterationsProgressStatusColumnName: string = 'IterationsProgressStatus';
    private readonly _deviationsCountColumnName: string = 'DeviationsCount';
    protected getRowClassFn = (): string => 'cursor-pointer';

    private fiChecklist2ReportHelperService = inject(FiChecklist2ReportHelperService);
    private ngZone = inject(NgZone);

    constructor(injector: Injector) {
        super(injector);
    }

    public ngOnInit(): void {
        this.checklistUnits$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((checklistUnits) => {
            this.checklistUnits = checklistUnits;
            this.ngZone.run(() => {
                this.reloadData();
            });
        });
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const change = changes.iterationStatusType;
        if (change && !change.isFirstChange()) {
            if (this.isIterationStatusSet()) {
                this.onSortChanged({
                    active: this._iterationsProgressStatusColumnName,
                    direction: MatSortDirection.Descending,
                });
            } else {
                this.onSortChanged({
                    active: this.defaultSortColumnName,
                    direction: MatSortDirection.Ascending,
                });
            }
        }
    }

    public getListData(): Promise<IChecklist2ReportHistoryUnits[]> {
        return Promise.resolve(this.checklistUnits);
    }

    protected getSortDataFromLocalStorage(): MatSort {
        // don't load sort from local storage, use default one (by business units)
        return undefined;
    }

    protected isIterationStatusSet(): boolean {
        return isDefined(this.iterationStatusType);
    }

    protected getUnitIterationsCount(checklistUnits: IChecklist2ReportHistoryUnits): number {
        return checklistUnits.IterationsCount;
        //return this.checklistUnitsIterationGroups[businessUnitId]?.length;
    }

    protected getDeviationsPercentage(checklistUnits: IChecklist2ReportHistoryUnits): number {
        return this.fiChecklist2ReportHelperService.getDeviationsPercentage(checklistUnits);
    }

    protected getOkAnswersPercentage(checklistUnits: IChecklist2ReportHistoryUnits): number {
        return this.fiChecklist2ReportHelperService.getOkAnswersPercentage(checklistUnits);
    }

    protected getDeviationsPercentageBadgeClass(checklistUnits: IChecklist2ReportHistoryUnits): {
        [key: string]: boolean;
    } {
        return {
            'deviation-border-gradient': this.getDeviationsPercentage(checklistUnits) > 0,
        };
    }

    protected getUnitCompletedPercentage(checklistUnits: IChecklist2ReportHistoryUnits): number {
        return this.getPercentage(
            this.getUnitCompletedCheckpointsCount(checklistUnits),
            this.getUnitTotalCheckpointsCount(checklistUnits),
        );
    }

    protected getUnitCompletedCheckpointsCount(checklistUnits: IChecklist2ReportHistoryUnits): number {
        return this.fiChecklist2ReportHelperService.getHistoryUnitsFilledCheckpoints(checklistUnits);
    }

    protected getUnitTotalCheckpointsCount(checklistUnits: IChecklist2ReportHistoryUnits): number {
        return this.fiChecklist2ReportHelperService.getHistoryUnitsTotalCheckpoints(checklistUnits);
    }

    protected checklistStatusData(checklistUnits: IChecklist2ReportHistoryUnits): IChecklistStatusProgressData {
        return this.fiChecklist2ReportHelperService.getStatusProgressDataFromUnits([checklistUnits]);
    }

    /*private getChecklistUnitStatusCount(checklistUnits: IItemWithBusinessUnitId[], businessUnitId: number): number {
        return checklistUnits.filter((unit: IItemWithBusinessUnitId) => {
            return unit.BusinessUnitId === businessUnitId
        }).length
    }*/

    protected getPercentage(number: number, total: number): number {
        return Math.round((number * 100) / total);
    }

    protected getSortOrderFn(): TSortOrder<IChecklist2ReportHistoryUnits> {
        // override if custom sort needed
        const sortColumn: string = this.selectedSort.active;

        if (sortColumn === this._deviationsCountColumnName) {
            return (item: IChecklist2ReportHistoryUnits) => {
                return this.fiChecklist2ReportHelperService.getDeviationsPercentage(item);
            };
        }

        if (sortColumn === this._iterationsProgressStatusColumnName) {
            return (item: IChecklist2ReportHistoryUnits) => {
                switch (this.iterationStatusType) {
                    case ChecklistReportPastChecklistChartSegmentIndex.Completed:
                        return item.CompletedIterationsCount;
                    case ChecklistReportPastChecklistChartSegmentIndex.InProgress:
                        return item.InProgressIterationsCount;
                    case ChecklistReportPastChecklistChartSegmentIndex.NotStarted:
                        return item.NotStartedIterationsCount;
                }
                /*const summaryUnits: IItemWithBusinessUnitId[] = this.getCurrentSummaryUnitsForSort();
                const filteredSummaryUnits  = summaryUnits.filter(unit => unit.BusinessUnitId === item.BusinessUnitId)
                return filteredSummaryUnits.length;*/
            };
        }

        return super.getSortOrderFn();
    }

    protected showDetailedAnswersReport(checklistUnit: IChecklist2ReportHistoryUnits, $event: MouseEvent): void {
        $event.stopPropagation();
        this.onShowDetailedAnswersReportByBusinessUnitId.emit(checklistUnit.BusinessUnitId);
    }

    protected showUnitMetrics(checklistUnit: IChecklist2ReportHistoryUnits): void {
        this.onShowUnitMetrics.emit(checklistUnit);
    }

    protected getCheckpointAnsweredAmountPercentage(checklistUnit: IChecklist2ReportHistoryUnits): number {
        if (this.isOkAnswersFilter()) {
            return this.getOkAnswersPercentage(checklistUnit);
        }

        if (this.isNotOkAnswersFilter()) {
            return this.getDeviationsPercentage(checklistUnit);
        }
    }

    protected isAnswersFiltered(): boolean {
        return this.isOkAnswersFilter() || this.isNotOkAnswersFilter();
    }

    private isOkAnswersFilter(): boolean {
        return this.currentCheckpointStatusTypesFilter.includes(Checkpoint2StatusType.Ok);
    }

    private isNotOkAnswersFilter(): boolean {
        return this.currentCheckpointStatusTypesFilter.includes(Checkpoint2StatusType.NotOk);
    }

    protected getAnswersProgressClassName(): { [key: string]: boolean } {
        return {
            normality: this.isOkAnswersFilter(),
            deviations: this.isNotOkAnswersFilter(),
        };
    }

    protected getNonCompliancePercentage(item: IChecklist2ReportHistoryUnits): number {
        return this.fiChecklist2ReportHelperService.getNonCompliancePercentage(item);
    }

    protected getNonCompliancePercentageText(item: IChecklist2ReportHistoryUnits): string {
        return this.fiChecklist2ReportHelperService.getNonCompliancePercentageText(item);
    }

    protected getCompliancePercentage(item: IChecklist2ReportHistoryUnits): number {
        return this.fiChecklist2ReportHelperService.getCompliancePercentage(item);
    }

    protected getCompliancePercentageText(item: IChecklist2ReportHistoryUnits): string {
        return this.fiChecklist2ReportHelperService.getCompliancePercentageText(item);
    }

    protected getNonComplianceCountBadgeClass(checklistUnit: IChecklist2ReportHistoryUnits): { [key: string]: boolean } {
        return {
            'non-compliance-border-gradient': this.getNonCompliancePercentage(checklistUnit) > 0,
        };
    }

    protected readonly FiInfoIconMode = FiInfoIconMode;
    protected readonly MatSortDirection = MatSortDirection;
}
